/**
 * DocGuardEngine — The orchestrator that ties CodebaseAnalyzer + DocMapper + DocGenerator.
 *
 * Single entry-point for the entire DocGuard system:
 *   - `sync()`   — Full pipeline: analyze code → map docs → generate updates → write files
 *   - `status()` — Quick report: what's stale, what's missing, coverage %
 *   - `update()` — Only update existing managed sections (no new files)
 *   - `diff()`   — Preview what would change without writing
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { CodebaseAnalyzer, type CodebaseSnapshot } from './CodebaseAnalyzer.js';
import { DocMapper, type DocMapping } from './DocMapper.js';
import { DocGenerator, type GeneratedDoc, type GeneratorOptions } from './DocGenerator.js';

// ─── Public types ────────────────────────────────────────────────────────────

export interface DocGuardSyncResult {
  success: boolean;
  /** Files that were created */
  created: string[];
  /** Files that were updated */
  updated: string[];
  /** Files that were skipped (no changes needed) */
  skipped: string[];
  /** Human-readable summary of each change */
  changes: string[];
  /** Timing */
  durationMs: number;
  /** Full status snapshot */
  status: DocGuardStatus;
}

export interface DocGuardStatus {
  /** Total doc files found */
  totalDocs: number;
  /** Docs with managed sections */
  managedDocs: number;
  /** Stale docs (reference removed/changed code) */
  staleDocs: { file: string; reason: string }[];
  /** Orphan docs (not linked from any index) */
  orphanDocs: string[];
  /** Code files with exports but no documentation */
  undocumentedFiles: string[];
  /** Documentation coverage percentage */
  coveragePercent: number;
  /** Codebase stats */
  codebaseStats: {
    totalFiles: number;
    totalExports: number;
    totalRoutes: number;
    totalEnvVars: number;
    framework: string | null;
  };
  timestamp: string;
}

export interface DocGuardDiff {
  /** Files that would be created */
  creates: { file: string; preview: string }[];
  /** Files that would be updated */
  updates: { file: string; sectionsChanged: string[]; preview: string }[];
  /** Total managed sections that would change */
  totalSectionsChanged: number;
}

export interface DocGuardEngineConfig {
  /** Extra glob patterns to ignore */
  ignorePatterns: string[];
  /** Generator options */
  generator: Partial<GeneratorOptions>;
  /** Whether to create a .vibecheck/docguard-snapshot.json cache */
  cacheSnapshot: boolean;
}

const DEFAULT_CONFIG: DocGuardEngineConfig = {
  ignorePatterns: [],
  generator: {},
  cacheSnapshot: true,
};

// ─── Engine ──────────────────────────────────────────────────────────────────

export class DocGuardEngine {
  private rootDir: string;
  private config: DocGuardEngineConfig;

  constructor(rootDir: string, config: Partial<DocGuardEngineConfig> = {}) {
    this.rootDir = rootDir;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Full sync: analyze → map → generate → write.
   */
  async sync(): Promise<DocGuardSyncResult> {
    const start = Date.now();
    const { snapshot, mapping, docs } = await this.pipeline();

    const created: string[] = [];
    const updated: string[] = [];
    const skipped: string[] = [];
    const changes: string[] = [];

    for (const doc of docs) {
      const absPath = path.join(this.rootDir, doc.file);

      if (doc.action === 'create') {
        await this.ensureDir(absPath);
        await fs.writeFile(absPath, doc.content, 'utf-8');
        created.push(doc.file);
        changes.push(`CREATE ${doc.file}: ${doc.changeSummary}`);
      } else if (doc.action === 'update') {
        // Only write if content actually changed
        const existing = await this.readFileOrNull(absPath);
        if (existing !== null && existing === doc.content) {
          skipped.push(doc.file);
        } else {
          await this.ensureDir(absPath);
          await fs.writeFile(absPath, doc.content, 'utf-8');
          updated.push(doc.file);
          changes.push(`UPDATE ${doc.file}: ${doc.changeSummary}`);
        }
      }
    }

    // Cache snapshot
    if (this.config.cacheSnapshot) {
      await this.cacheSnapshotToDisk(snapshot);
    }

    const status = this.buildStatus(snapshot, mapping);

    return {
      success: true,
      created,
      updated,
      skipped,
      changes,
      durationMs: Date.now() - start,
      status,
    };
  }

  /**
   * Quick status check — no file writes.
   */
  async status(): Promise<DocGuardStatus> {
    const { snapshot, mapping } = await this.analyzeAndMap();
    return this.buildStatus(snapshot, mapping);
  }

  /**
   * Only update existing managed sections — no new files created.
   */
  async update(): Promise<DocGuardSyncResult> {
    const start = Date.now();
    const { snapshot, mapping } = await this.analyzeAndMap();

    const generator = new DocGenerator(snapshot, mapping, {
      ...this.config.generator,
      readme: true,
      apiDocs: false,
      envDocs: false,
      structureDocs: false,
      updateManaged: true,
      overwrite: false,
    });

    const docs = generator.generate();
    const updated: string[] = [];
    const skipped: string[] = [];
    const changes: string[] = [];

    for (const doc of docs) {
      if (doc.action !== 'update') continue;
      const absPath = path.join(this.rootDir, doc.file);
      const existing = await this.readFileOrNull(absPath);
      if (existing !== null && existing === doc.content) {
        skipped.push(doc.file);
      } else if (existing !== null) {
        await fs.writeFile(absPath, doc.content, 'utf-8');
        updated.push(doc.file);
        changes.push(`UPDATE ${doc.file}: ${doc.changeSummary}`);
      }
    }

    return {
      success: true,
      created: [],
      updated,
      skipped,
      changes,
      durationMs: Date.now() - start,
      status: this.buildStatus(snapshot, mapping),
    };
  }

  /**
   * Preview what would change — no file writes.
   */
  async diff(): Promise<DocGuardDiff> {
    const { snapshot, mapping, docs } = await this.pipeline();

    const creates: DocGuardDiff['creates'] = [];
    const updates: DocGuardDiff['updates'] = [];
    let totalSectionsChanged = 0;

    for (const doc of docs) {
      if (doc.action === 'create') {
        creates.push({
          file: doc.file,
          preview: doc.content.substring(0, 500) + (doc.content.length > 500 ? '\n...' : ''),
        });
      } else {
        const absPath = path.join(this.rootDir, doc.file);
        const existing = await this.readFileOrNull(absPath);
        if (existing !== null && existing !== doc.content) {
          updates.push({
            file: doc.file,
            sectionsChanged: doc.managedSections,
            preview: doc.changeSummary,
          });
          totalSectionsChanged += doc.managedSections.length;
        }
      }
    }

    return { creates, updates, totalSectionsChanged };
  }

  // ─── Internal pipeline ───────────────────────────────────────────────────

  private async analyzeAndMap(): Promise<{ snapshot: CodebaseSnapshot; mapping: DocMapping }> {
    const analyzer = new CodebaseAnalyzer(this.rootDir, this.config.ignorePatterns);
    const snapshot = await analyzer.analyze();

    const mapper = new DocMapper(this.rootDir, this.config.ignorePatterns);
    const mapping = await mapper.map(snapshot);

    return { snapshot, mapping };
  }

  private async pipeline(): Promise<{
    snapshot: CodebaseSnapshot;
    mapping: DocMapping;
    docs: GeneratedDoc[];
  }> {
    const { snapshot, mapping } = await this.analyzeAndMap();
    const generator = new DocGenerator(snapshot, mapping, this.config.generator);
    const docs = generator.generate();
    return { snapshot, mapping, docs };
  }

  private buildStatus(snapshot: CodebaseSnapshot, mapping: DocMapping): DocGuardStatus {
    return {
      totalDocs: mapping.stats.totalDocs,
      managedDocs: mapping.docs.filter(d => d.managedSections.length > 0).length,
      staleDocs: mapping.staleDocs.map(s => ({ file: s.docFile, reason: s.reason })),
      orphanDocs: mapping.orphanDocs,
      undocumentedFiles: mapping.undocumentedFiles.slice(0, 50),
      coveragePercent: mapping.stats.coveragePercent,
      codebaseStats: {
        totalFiles: snapshot.stats.totalFiles,
        totalExports: snapshot.stats.totalExports,
        totalRoutes: snapshot.stats.totalRoutes,
        totalEnvVars: snapshot.stats.totalEnvVars,
        framework: snapshot.framework,
      },
      timestamp: new Date().toISOString(),
    };
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private async ensureDir(filePath: string): Promise<void> {
    await fs.mkdir(path.dirname(filePath), { recursive: true });
  }

  private async readFileOrNull(filePath: string): Promise<string | null> {
    try {
      return await fs.readFile(filePath, 'utf-8');
    } catch { return null; }
  }

  private async cacheSnapshotToDisk(snapshot: CodebaseSnapshot): Promise<void> {
    try {
      const cacheDir = path.join(this.rootDir, '.vibecheck');
      await fs.mkdir(cacheDir, { recursive: true });
      const cachePath = path.join(cacheDir, 'docguard-snapshot.json');
      // Strip content to keep cache small
      const slim = {
        ...snapshot,
        modules: snapshot.modules.map(m => ({
          file: m.file,
          exports: m.exports.map(e => ({ name: e.name, kind: e.kind })),
          loc: m.loc,
          language: m.language,
        })),
      };
      await fs.writeFile(cachePath, JSON.stringify(slim, null, 2), 'utf-8');
    } catch { /* non-critical */ }
  }
}
